import React, { useState, useCallback, useMemo } from 'react';
import {
  Card,
  Typography,
  Button,
  Tag,
  Space,
  Empty,
  Spin,
  Tooltip,
  Collapse,
  notification,
  Modal,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  HistoryOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  useLatestStrategyGuide,
  useStrategyGuideVersions,
  StrategyGuideStatuses,
} from '@aims/shared/strategy-guides';
import sharedSettings from '@aims/shared/sharedSettings';
import filesize from 'filesize';
import StrategyGuideUploadModal from './StrategyGuideUploadModal';
import StrategyGuideReviewModal from './StrategyGuideReviewModal';
import StrategyGuideNotesModal from './StrategyGuideNotesModal';
import StrategyGuidePreviewModal from './StrategyGuidePreviewModal';
import ConfirmNewVersionModal from './ConfirmNewVersionModal';
import { deleteStrategyGuideMutation } from './constants';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { confirm } = Modal;

function StrategyGuideTab({ contactId, contact }) {
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewGuide, setPreviewGuide] = useState(null);
  const [rejectionContext, setRejectionContext] = useState(null);

  const { strategyGuide, loading, refetch } = useLatestStrategyGuide(contactId);
  const {
    versions,
    loading: loadingVersions,
    refetch: refetchVersions,
  } = useStrategyGuideVersions(contactId);

  const [deleteStrategyGuide, { loading: deleting }] = useMutation(
    deleteStrategyGuideMutation,
  );

  const handleDelete = useCallback(() => {
    if (!strategyGuide) return;

    confirm({
      title: 'Delete Strategy Guide?',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete v${strategyGuide.version} of the strategy guide? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        return deleteStrategyGuide({
          variables: { _id: strategyGuide._id },
        })
          .then(() => {
            notification.success({
              message: 'Success',
              description: 'Strategy guide deleted successfully.',
            });
            // Small delay to allow backend to process deletion
            setTimeout(() => {
              refetch();
              refetchVersions();
            }, 500);
          })
          .catch((err) => {
            console.error(err);
            notification.error({
              message: 'Error',
              description: 'Failed to delete strategy guide.',
            });
            // Re-throw to keep modal open on error
            throw err;
          });
      },
    });
  }, [strategyGuide, deleteStrategyGuide, refetch, refetchVersions]);

  const handlePreview = useCallback((guide) => {
    setPreviewGuide(guide);
    setPreviewModalVisible(true);
  }, []);

  const handleUploadClick = useCallback(() => {
    // If no existing guide or latest is pending changes, go straight to upload
    if (!strategyGuide || strategyGuide.status === 'PENDING_CHANGES') {
      setRejectionContext(null);
      setUploadModalVisible(true);
    } else {
      // Show confirmation modal for DRAFT or APPROVED
      setConfirmModalVisible(true);
    }
  }, [strategyGuide]);

  const handleConfirmNewVersion = useCallback((context) => {
    setRejectionContext(context);
    setConfirmModalVisible(false);
    setUploadModalVisible(true);
  }, []);

  const handleUploadComplete = useCallback(() => {
    setUploadModalVisible(false);
    setRejectionContext(null);
    refetch();
    refetchVersions();
  }, [refetch, refetchVersions]);

  const handleUploadCancel = useCallback(() => {
    setUploadModalVisible(false);
    setRejectionContext(null);
  }, []);

  const handleReviewComplete = useCallback(() => {
    setReviewModalVisible(false);
    refetch();
    refetchVersions();
  }, [refetch, refetchVersions]);

  const handleNotesComplete = useCallback(() => {
    setNotesModalVisible(false);
    refetch();
  }, [refetch]);

  const statusTag = useMemo(() => {
    if (!strategyGuide) return null;
    const status = StrategyGuideStatuses[strategyGuide.status];
    return (
      <Tag color={status?.color || 'default'}>
        {status?.label || strategyGuide.status}
      </Tag>
    );
  }, [strategyGuide]);

  const dateFormat = useMemo(() => {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 16, alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          <FileTextOutlined style={{ marginRight: 8 }} />
          Strategy Guide
        </Title>
        <div style={{ flex: 1 }} />
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleUploadClick}
        >
          {strategyGuide ? 'Upload New Version' : 'Upload Strategy Guide'}
        </Button>
      </div>

      {!strategyGuide ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No strategy guide uploaded yet"
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUploadClick}
            >
              Upload Strategy Guide
            </Button>
          </Empty>
        </Card>
      ) : (
        <>
          {/* Current Strategy Guide Card */}
          <Card
            style={{ marginBottom: 24 }}
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text strong>Current Version</Text>
                <Tag
                  color={sharedSettings.colors.primary}
                  style={{ marginLeft: 8 }}
                >
                  v{strategyGuide.version}
                </Tag>
                {statusTag}
              </div>
            }
            extra={
              <Space>
                {strategyGuide.file && (
                  <>
                    <Tooltip title="Preview">
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => handlePreview(strategyGuide)}
                      />
                    </Tooltip>
                    <Tooltip title="Download">
                      <Button
                        icon={<DownloadOutlined />}
                        href={strategyGuide.file.url}
                        target="_blank"
                        download={
                          strategyGuide.fileName || strategyGuide.file.filename
                        }
                      />
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Edit Notes">
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => setNotesModalVisible(true)}
                  />
                </Tooltip>
                <Tooltip title="Review">
                  <Button
                    icon={<CheckOutlined />}
                    onClick={() => setReviewModalVisible(true)}
                  />
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    onClick={handleDelete}
                    loading={deleting}
                  />
                </Tooltip>
              </Space>
            }
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <div style={{ minWidth: 200 }}>
                <Text type="secondary">File Name</Text>
                <Paragraph style={{ marginBottom: 0 }}>
                  {strategyGuide.fileName ||
                    strategyGuide.file?.filename ||
                    'N/A'}
                </Paragraph>
              </div>
              <div style={{ minWidth: 120 }}>
                <Text type="secondary">File Size</Text>
                <Paragraph style={{ marginBottom: 0 }}>
                  {strategyGuide.file?.size
                    ? filesize(strategyGuide.file.size, { round: 1 })
                    : 'N/A'}
                </Paragraph>
              </div>
              <div style={{ minWidth: 180 }}>
                <Text type="secondary">Uploaded</Text>
                <Paragraph style={{ marginBottom: 0 }}>
                  {strategyGuide.createdAt
                    ? dateFormat.format(new Date(strategyGuide.createdAt))
                    : 'N/A'}
                </Paragraph>
              </div>
              <div style={{ minWidth: 180 }}>
                <Text type="secondary">Uploaded By</Text>
                <Paragraph style={{ marginBottom: 0 }}>
                  {strategyGuide.createdByUser?.name || 'N/A'}
                </Paragraph>
              </div>
              {strategyGuide.reviewedAt && (
                <>
                  <div style={{ minWidth: 180 }}>
                    <Text type="secondary">Reviewed</Text>
                    <Paragraph style={{ marginBottom: 0 }}>
                      {dateFormat.format(new Date(strategyGuide.reviewedAt))}
                    </Paragraph>
                  </div>
                  <div style={{ minWidth: 180 }}>
                    <Text type="secondary">Reviewed By</Text>
                    <Paragraph style={{ marginBottom: 0 }}>
                      {strategyGuide.reviewedByUser?.name || 'N/A'}
                    </Paragraph>
                  </div>
                </>
              )}
            </div>

            {strategyGuide.notes && (
              <div
                style={{
                  marginTop: 16,
                  paddingTop: 16,
                  borderTop: `1px solid ${sharedSettings.colors.borderGray}`,
                }}
              >
                <Text type="secondary">Notes</Text>
                <div
                  style={{ marginTop: 8 }}
                  dangerouslySetInnerHTML={{ __html: strategyGuide.notes }}
                />
              </div>
            )}
          </Card>

          {/* Version History */}
          {versions && versions.length > 1 && (
            <Collapse ghost style={{ marginBottom: 24 }}>
              <Panel
                header={
                  <span>
                    <HistoryOutlined style={{ marginRight: 8 }} />
                    Version History ({versions.length} versions)
                  </span>
                }
                key="history"
              >
                {versions.map((version) => (
                  <Card
                    key={version._id}
                    size="small"
                    style={{ marginBottom: 8 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Tag
                        color={
                          version._id === strategyGuide._id
                            ? sharedSettings.colors.primary
                            : 'default'
                        }
                      >
                        v{version.version}
                      </Tag>
                      <Tag color={StrategyGuideStatuses[version.status]?.color}>
                        {StrategyGuideStatuses[version.status]?.label}
                      </Tag>
                      <Text style={{ marginLeft: 8 }}>
                        {version.fileName || version.file?.filename}
                      </Text>
                      <Text type="secondary" style={{ marginLeft: 16 }}>
                        {version.createdAt
                          ? dateFormat.format(new Date(version.createdAt))
                          : ''}
                      </Text>
                      <div style={{ flex: 1 }} />
                      <Space>
                        {version.file && (
                          <>
                            <Tooltip title="Preview">
                              <Button
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => handlePreview(version)}
                              />
                            </Tooltip>
                            <Tooltip title="Download">
                              <Button
                                size="small"
                                icon={<DownloadOutlined />}
                                href={version.file.url}
                                target="_blank"
                                download={
                                  version.fileName || version.file.filename
                                }
                              />
                            </Tooltip>
                          </>
                        )}
                      </Space>
                    </div>
                  </Card>
                ))}
              </Panel>
            </Collapse>
          )}
        </>
      )}

      {/* Modals */}
      <ConfirmNewVersionModal
        visible={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        onConfirm={handleConfirmNewVersion}
        latestGuide={strategyGuide}
      />

      <StrategyGuideUploadModal
        visible={uploadModalVisible}
        onCancel={handleUploadCancel}
        onComplete={handleUploadComplete}
        contactId={contactId}
        contact={contact}
        currentVersion={strategyGuide?.version || 0}
        rejectPreviousId={rejectionContext?.rejectPreviousId}
        rejectionNotes={rejectionContext?.rejectionNotes}
      />

      <StrategyGuideReviewModal
        visible={reviewModalVisible}
        onCancel={() => setReviewModalVisible(false)}
        onComplete={handleReviewComplete}
        strategyGuide={strategyGuide}
      />

      <StrategyGuideNotesModal
        visible={notesModalVisible}
        onCancel={() => setNotesModalVisible(false)}
        onComplete={handleNotesComplete}
        strategyGuide={strategyGuide}
      />

      <StrategyGuidePreviewModal
        visible={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        strategyGuide={previewGuide}
        onReviewComplete={handleReviewComplete}
      />
    </div>
  );
}

export default StrategyGuideTab;
